import { motion } from "framer-motion";
import {
  Boxes,
  Scissors,
  CircuitBoard,
  Bot,
  Video,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { EQUIPMENT_CATEGORIES, EQUIPMENT_TIERS } from "../lib/catalog";
import { CALENDLY_URL } from "../lib/site";

const ICONS = { Boxes, Scissors, CircuitBoard, Bot, Video, ShieldCheck };

export default function EquipmentPage() {
  return (
    <div data-testid="equipment-page">
      <PageHero
        eyebrow="Equipment Guide"
        accent="#0055FF"
        title={
          <>
            What great{" "}
            <span className="bg-[#00C2B8] px-2 inline-block">kit</span> looks
            like.
          </>
        }
        intro="Six categories of equipment we recommend and install in schools and libraries. Prices are indicative AUD street prices as of today — we help you source, bulk-buy and set it up."
        ctaLabel="Book a sourcing call"
        ctaTo={CALENDLY_URL}
        ctaExternal
        secondaryLabel="Audit your space"
        secondaryTo="/checklist"
      />

      {/* Tier legend */}
      <section className="bg-white border-b-2 border-[#1A1A1A]">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(EQUIPMENT_TIERS).map(([tier, info]) => (
            <div
              key={tier}
              className="rounded-2xl border-2 border-[#1A1A1A] p-4 nb-shadow-sm"
              style={{ backgroundColor: `${info.color}22` }}
              data-testid={`tier-legend-${tier.toLowerCase()}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: info.color }}
                />
                <span className="font-mono text-[11px] uppercase tracking-widest font-bold">
                  {tier}
                </span>
              </div>
              <p className="mt-2 text-sm font-semibold">{info.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      {EQUIPMENT_CATEGORIES.map((cat, idx) => {
        const Icon = ICONS[cat.icon] || Boxes;
        const flip = idx % 2 === 1;
        return (
          <section
            key={cat.key}
            data-testid={`equipment-category-${cat.key}`}
            className={`${
              flip ? "bg-[#F8F9FA]" : "bg-white"
            } border-b-2 border-[#1A1A1A]`}
          >
            <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-14 md:py-20">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4">
                  <div
                    className="inline-flex items-center justify-center h-14 w-14 rounded-2xl border-2 border-[#1A1A1A] nb-shadow-sm"
                    style={{ backgroundColor: cat.accent, color: cat.accent === "#1A1A1A" ? "#FFD700" : "#1A1A1A" }}
                  >
                    <Icon size={24} strokeWidth={2.5} />
                  </div>
                  <p className="mt-5 font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">
                    Category 0{idx + 1}
                  </p>
                  <h2 className="mt-1 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter">
                    {cat.name}
                  </h2>
                </div>
                <div className="lg:col-span-8 space-y-4">
                  {cat.items.map((item, i) => {
                    const tierInfo = EQUIPMENT_TIERS[item.tier] || { color: "#1A1A1A" };
                    return (
                      <motion.article
                        key={item.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: i * 0.06 }}
                        data-testid={`equipment-item-${cat.key}-${i}`}
                        className="rounded-2xl bg-white border-2 border-[#1A1A1A] p-5 md:p-6 nb-shadow-sm nb-lift"
                      >
                        <div className="flex items-start justify-between flex-wrap gap-3">
                          <h3 className="font-display font-black text-xl md:text-2xl tracking-tight leading-tight max-w-md">
                            {item.name}
                          </h3>
                          <span
                            className="inline-flex items-center gap-2 border-2 border-[#1A1A1A] rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest font-mono"
                            style={{ backgroundColor: tierInfo.color }}
                          >
                            {item.tier}
                          </span>
                        </div>
                        <p className="mt-3 text-sm md:text-base text-[#4A4A4A] font-semibold leading-relaxed">
                          {item.blurb}
                        </p>
                        <p className="mt-3 font-mono text-xs uppercase tracking-widest font-bold text-[#0055FF]">
                          Indicative AUD · {item.price}
                        </p>
                      </motion.article>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA strip */}
      <section className="bg-[#FFD700] border-b-2 border-[#1A1A1A]">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <h2 className="font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter">
              Not sure what you actually need?
            </h2>
            <p className="mt-4 text-base md:text-lg text-[#1A1A1A]/80 font-semibold max-w-xl">
              We’ll audit your space, map it against your year levels and
              curriculum goals, and give you a prioritised shopping list — all
              inside a free discovery call.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="equipment-cta-call"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-6 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press"
            >
              Book a sourcing call <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>
            <Link
              to="/checklist"
              data-testid="equipment-cta-checklist"
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-6 py-3 font-bold uppercase tracking-wide nb-shadow-sm nb-press"
            >
              Use the checklist
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
